import React from "react";
import PropTypes from "prop-types";
import { LoadingSpinner } from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import RunFunnel, { previewToFunnelCounts } from "./RunFunnel";
import { formatCount, formatPolicyReason } from "./automationUtils";

/**
 * Right-hand panel of the editor: the debounced, read-only preview of the
 * current form — funnel "Match → Would enter → Guards → Policy → Would
 * enqueue", the policy-reason breakdown and a sample of the users.
 *
 * status: "invalid" (client errors, nothing requested) | "loading" |
 *         "success" | "error" (backend 400) | "idle"
 */
const LivePreviewPanel = ({
  status = "idle",
  data = null,
  error = "",
  clientErrors = [],
  isEditingSaved = false,
  stale = false,
  triggerType = "routine",
  className = "",
}) => {
  const isEventPreview = triggerType === "event" || data?.trigger_type === "event";
  const counts = data && !isEventPreview ? previewToFunnelCounts(data) : null;
  const reasons = Object.entries(data?.policy_reasons || {}).sort((a, b) => b[1] - a[1]);
  const sample = Array.isArray(data?.sample_users) ? data.sample_users : [];

  return (
    <aside
      className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4 space-y-4 ${className}`}
      aria-label="Live preview"
      aria-busy={status === "loading"}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className={`${typography.h5} ${textColors.primary}`}>Live preview</h3>
          <p className={`${typography.caption} ${textColors.tertiary}`}>
            {isEventPreview
              ? "Read-only — describes when this automation fires. Nothing is saved or sent."
              : "Read-only — evaluated against real users as you type. Nothing is saved or sent."}
          </p>
        </div>
        <div className={`${typography.caption} ${textColors.tertiary} whitespace-nowrap`} role="status" aria-live="polite">
          {status === "loading" ? (
            <span className="inline-flex items-center gap-1.5">
              <LoadingSpinner size="xs" /> Updating…
            </span>
          ) : status === "success" ? (
            stale ? "Out of date" : "Up to date"
          ) : null}
        </div>
      </div>

      {status === "invalid" ? (
        <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-3`}>
          <p className={`${typography.bodySmall} ${textColors.secondary}`}>Complete the form to see who would be reached:</p>
          <ul className={`mt-2 space-y-1 list-disc pl-5 ${typography.caption} ${textColors.tertiary}`}>
            {clientErrors.slice(0, 6).map((e) => (
              <li key={`${e.field}-${e.message}`}>{e.message}</li>
            ))}
            {clientErrors.length > 6 ? <li>…and {clientErrors.length - 6} more</li> : null}
          </ul>
        </div>
      ) : null}

      {status === "error" ? (
        <div
          className="rounded-xl px-3 py-2 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800"
          role="alert"
        >
          <p className={`${typography.bodySmall} text-red-800 dark:text-red-300 break-words`}>Preview failed: {error}</p>
        </div>
      ) : null}

      {status === "idle" ? (
        <p className={`${typography.bodySmall} ${textColors.muted}`}>Start filling in the form to see a preview.</p>
      ) : null}

      {status === "loading" && !data ? (
        <div className="animate-pulse space-y-3" aria-hidden="true">
          <div className={`h-3 ${surfaces.tertiary} rounded w-3/4`} />
          <div className={`h-3 ${surfaces.tertiary} rounded w-1/2`} />
          <div className={`h-3 ${surfaces.tertiary} rounded w-5/6`} />
          <div className={`h-3 ${surfaces.tertiary} rounded w-2/3`} />
        </div>
      ) : null}

      {data && isEventPreview ? (
        <div
          className={`space-y-3 transition-opacity ${stale || status === "loading" ? "opacity-60" : "opacity-100"}`}
          data-testid="event-preview"
        >
          <div className={`rounded-xl border ${borders.default} ${surfaces.primary} p-3`}>
            <div className={`${typography.caption} ${textColors.tertiary} uppercase tracking-wider mb-1`}>Fires on</div>
            <div className={`font-heading text-xl font-bold ${textColors.primary}`}>
              {data.trigger_event_label || data.trigger_event || "—"}
            </div>
            <p className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>
              Every time this happens to a user, immediately — guards and the delivery policy still apply.
            </p>
          </div>
          <div className={`rounded-xl border ${borders.default} ${surfaces.primary} p-3`}>
            <div className={`${typography.caption} ${textColors.tertiary} uppercase tracking-wider mb-1`}>Recent volume</div>
            {data.recent_occurrences === null || data.recent_occurrences === undefined ? (
              <p className={`${typography.bodySmall} ${textColors.secondary}`}>No estimate available for this event.</p>
            ) : (
              <p className={`${typography.bodySmall} ${textColors.primary}`}>
                <span className="font-heading text-2xl font-bold tabular-nums">{formatCount(data.recent_occurrences)}</span>
                <span className={`ml-2 ${textColors.secondary}`}>
                  time{Number(data.recent_occurrences) === 1 ? "" : "s"} in the last {data.recent_occurrences_days || 30} days
                </span>
              </p>
            )}
            <p className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>
              An estimate from existing records, so you can gauge how often this automation would fire.
            </p>
          </div>
        </div>
      ) : null}

      {data && counts ? (
        <div className={`space-y-4 transition-opacity ${stale || status === "loading" ? "opacity-60" : "opacity-100"}`}>
          <div className={`rounded-xl border ${borders.default} ${surfaces.primary} p-3`}>
            <div className={`${typography.caption} ${textColors.tertiary} uppercase tracking-wider mb-1`}>If this ran right now</div>
            <div className={`font-heading text-2xl font-bold ${textColors.primary} tabular-nums`}>
              {formatCount(counts.enqueued)}
              <span className={`ml-2 ${typography.bodySmall} ${textColors.secondary} font-normal`}>
                user{counts.enqueued === 1 ? "" : "s"} would be enqueued
              </span>
            </div>
            <p className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>
              out of {formatCount(counts.matched)} currently in the situation
              {isEditingSaved ? " (entries exclude users already inside this automation)" : ""}.
            </p>
          </div>

          <RunFunnel counts={counts} mode="preview" variant="full" />

          <div>
            <div className={`${typography.label} ${textColors.secondary} mb-1`}>Policy breakdown</div>
            {reasons.length === 0 ? (
              <p className={`${typography.caption} ${textColors.tertiary}`}>
                Nothing held back by the delivery policy.
              </p>
            ) : (
              <ul className="space-y-1">
                {reasons.map(([reason, count]) => (
                  <li key={reason} className="flex items-center justify-between gap-3">
                    <span className={`${typography.caption} ${textColors.secondary} truncate`} title={reason}>
                      {formatPolicyReason(reason)}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 tabular-nums">
                      {formatCount(count)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <div className={`${typography.label} ${textColors.secondary} mb-1`}>
              Sample users{sample.length ? ` (${sample.length})` : ""}
            </div>
            {sample.length === 0 ? (
              <p className={`${typography.caption} ${textColors.tertiary}`}>No one would be enqueued right now.</p>
            ) : (
              <ul className={`rounded-xl border ${borders.default} ${surfaces.primary} divide-y ${borders.light} max-h-64 overflow-y-auto`}>
                {sample.map((u) => (
                  <li key={u.id ?? `${u.username}-${u.email}`} className="px-3 py-1.5 min-w-0">
                    <div className={`${typography.bodySmall} ${textColors.primary} font-medium truncate`}>{u.username || "—"}</div>
                    <div className={`${typography.caption} ${textColors.tertiary} truncate`} title={u.email || ""}>
                      {u.email || "—"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </aside>
  );
};

LivePreviewPanel.propTypes = {
  status: PropTypes.oneOf(["idle", "invalid", "loading", "success", "error"]),
  data: PropTypes.object,
  error: PropTypes.string,
  clientErrors: PropTypes.arrayOf(PropTypes.shape({ field: PropTypes.string, message: PropTypes.string })),
  isEditingSaved: PropTypes.bool,
  stale: PropTypes.bool,
  triggerType: PropTypes.oneOf(["routine", "event"]),
  className: PropTypes.string,
};

export default LivePreviewPanel;
